"use client";

import Image from "next/image";

import { ChevronDown } from "lucide-react";

import { cn } from "@/lib/utils";

import type { MarketData } from "@/types";
import type { Column, ColumnDef, Row } from "@tanstack/react-table";

export const columns: ColumnDef<MarketData>[] = [
  {
    accessorKey: "name",
    header: "Name",
    cell: ({ row }) => (
      <div className="flex items-center">
        <Image
          src={`https://s2.coinmarketcap.com/static/img/coins/64x64/${row.original.id}.png`}
          alt={row.getValue("name")}
          width={128}
          height={128}
          className="size-4 rounded-full"
        />
        <span className="ml-2">{row.getValue("name")}</span>
        <span className="text-foreground">&nbsp;({row.original.symbol})</span>
      </div>
    ),
  },
  {
    accessorKey: "price",
    header: ({ column }) => <SortedHeader column={column}>Price</SortedHeader>,
    cell: ({ row }) => formatPrice(row, "price"),
  },
  {
    accessorKey: "1h",
    header: "1h %",
    cell: ({ row }) => formatPercentage(row, "1h"),
  },
  {
    accessorKey: "24h",
    header: "24h %",
    cell: ({ row }) => formatPercentage(row, "24h"),
  },
  {
    accessorKey: "7d",
    header: "7d %",
    cell: ({ row }) => formatPercentage(row, "7d"),
  },
  {
    accessorKey: "marketCap",
    header: ({ column }) => (
      <SortedHeader column={column}>Market Cap</SortedHeader>
    ),
    cell: ({ row }) => formatPrice(row, "marketCap"),
  },
  {
    accessorKey: "volume",
    header: ({ column }) => <SortedHeader column={column}>Volume</SortedHeader>,
    cell: ({ row }) => formatPrice(row, "volume"),
  },
  {
    accessorKey: "circulatingSupply",
    header: "Circulating Supply",
    cell: ({ row }) => {
      const formattedValue = new Intl.NumberFormat("en-US").format(
        row.getValue("circulatingSupply"),
      );

      return (
        <>
          <span>{formattedValue}</span>
          <span className="text-foreground">&nbsp;({row.original.symbol})</span>
        </>
      );
    },
  },
];

function SortedHeader({
  column,
  children,
}: {
  column: Column<MarketData, unknown>;
  children: React.ReactNode;
}) {
  return (
    <button
      className="flex items-center gap-2"
      onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
    >
      {children}
      <ChevronDown
        className={cn("h-3 w-3 transition-transform duration-200", {
          "rotate-180": column.getIsSorted() === "asc",
        })}
      />
    </button>
  );
}

function formatPercentage(row: Row<MarketData>, name: string) {
  const value: string = row.getValue(name);
  const isDown = String(value).startsWith("-");

  const formattedValue = new Intl.NumberFormat("en-US").format(Number(value));

  return (
    <span
      className={cn("text-success", {
        "text-destructive": isDown,
      })}
    >
      {isDown ? "" : "+"}
      {formattedValue}%
    </span>
  );
}

function formatPrice(row: Row<MarketData>, name: string) {
  const formatted = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(row.getValue(name));

  return formatted;
}
